import { makeObservable, observable, action, computed, runInAction, reaction } from 'mobx';
import { getEffectiveNavigation, type EffectiveElsewhere, type EffectiveNavigation } from '@/api/studioModules';
import {
  DESTINATIONS,
  HOME_KEY,
  STUDIO_MANIFEST_VERSION,
  destinationByKey,
  findDestination,
  type Destination,
} from '@/layouts/navigation';
import { readPreference, writePreference } from '@/lib/preferences';
import type { RootStore } from './RootStore';

// ─────────────────────────────────────────────────────────────────────────────
// THE resolved navigation model. One object answers, for the signed-in person:
// which modules they can launch, which destinations each holds (only ones they
// may open), where a URL belongs, and what to show when it belongs nowhere.
// The rail, the module launcher, the breadcrumbs, ⌘K and pins all read it —
// none keeps its own list.
//
//   server  GET api/AgentStudioModules/Effective  → placement + access, per user
//   client  layouts/navigation.ts DESTINATIONS    → what this build can open
//   model   = server placement ∩ client capability (a key either side lacks is
//             never rendered; admins see the drift on Administration → Modules)
//
// The ACTIVE module is derived from the URL, never stored: a deep link, reload or
// back/forward always lands in the module that owns the page. The last module
// someone used is only a preference for pages that belong to no module (Home).
//
// Failure is honest: no catalog → no module menus (never "show everything"), a
// retry, and the routed page keeps rendering under its own guards — a catalog
// load never remounts the page, so drafts and running jobs are untouched.
// ─────────────────────────────────────────────────────────────────────────────

const REFRESH_AFTER_MS = 60_000;
const LAST_MODULE_PREF = 'nav.lastModule';

export interface ResolvedModule {
  key: string;
  title: string;
  description?: string | null;
  iconKey: string;
  color?: string | null;
  /** The destination opening this module lands on (already one this person may open). */
  landing: Destination;
  items: Destination[];
}

export type PathPlacement =
  | { kind: 'home'; destination: Destination }
  | { kind: 'module'; destination: Destination; module: ResolvedModule }
  /** Openable, but in no launchable module: a hidden or empty module, or not placed yet. */
  | { kind: 'elsewhere'; destination: Destination; info: EffectiveElsewhere }
  /** A page this build has, that the catalog did not offer this person (no access, or no catalog). */
  | { kind: 'not-in-navigation'; destination: Destination }
  | { kind: 'unknown' };

export class StudioModuleStore {
  @observable status: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  @observable error: string | null = null;
  @observable.ref nav: EffectiveNavigation | null = null;
  @observable lastModuleKey: string | null = null;
  /** A refresh is in flight behind a model already on screen. */
  @observable refreshing = false;

  private loadedAt = 0;
  /** Bumps on every load and identity change, so a late answer for an older request is ignored. */
  private requestSeq = 0;
  private subject: string | number | null = null;

  constructor(private root: RootStore) {
    makeObservable(this);
    // Navigation belongs to an identity: a sign-out or a different account drops it (and its preferences) at once.
    reaction(
      () => this.root.authStore.user?.userId ?? null,
      (userId) => this.identityChanged(userId),
      { fireImmediately: true },
    );
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', () => {
        if (this.status === 'ready' && Date.now() - this.loadedAt > REFRESH_AFTER_MS) void this.load();
      });
    }
  }

  @action private identityChanged(userId: string | number | null): void {
    this.requestSeq++;
    this.subject = userId;
    this.nav = null;
    this.error = null;
    this.status = 'idle';
    this.refreshing = false;
    this.loadedAt = 0;
    this.lastModuleKey = readPreference<string>(LAST_MODULE_PREF, userId);
  }

  /**
   * Loads (or refreshes) this person's navigation. A refresh keeps the current model on screen until the new one arrives,
   * and a stale notice from an earlier failed refresh stays up until this one answers.
   */
  async load(): Promise<void> {
    if (!this.root.authStore.isAuthenticated) return;
    const seq = ++this.requestSeq;
    runInAction(() => {
      this.status = this.nav ? 'ready' : 'loading';
      this.refreshing = !!this.nav;
      if (!this.nav) this.error = null;
    });
    try {
      const nav = await getEffectiveNavigation();
      if (seq !== this.requestSeq) return;
      runInAction(() => {
        this.nav = nav;
        this.status = 'ready';
        this.error = null;
        this.refreshing = false;
        this.loadedAt = Date.now();
      });
    } catch (err) {
      if (seq !== this.requestSeq) return;
      runInAction(() => {
        // A failed REFRESH keeps the model already shown (it was authorised when fetched) and says it may be out of date
        // (`isStale`); a failed first load shows none — never an unrestricted fallback.
        this.error = (err as Error).message;
        this.status = this.nav ? 'ready' : 'error';
        this.refreshing = false;
      });
    }
  }

  /** The navigation on screen is the last one fetched: a refresh since then failed. Menus still show it; say so, offer Retry. */
  @computed get isStale(): boolean {
    return this.status === 'ready' && !!this.nav && this.error != null;
  }

  ensureLoaded(): void {
    if (this.status === 'idle') void this.load();
  }

  // ── The model ──────────────────────────────────────────────────────────────

  /** Launchable modules, in launcher order, each with only destinations this build can open. */
  @computed get modules(): ResolvedModule[] {
    if (!this.nav) return [];
    const out: ResolvedModule[] = [];
    for (const m of this.nav.modules) {
      const items = m.items.map((i) => destinationByKey(i.navKey)).filter((d): d is Destination => !!d && d.key !== HOME_KEY);
      if (items.length === 0) continue;
      out.push({
        key: m.key,
        title: m.title,
        description: m.description,
        iconKey: m.iconKey,
        color: m.color,
        landing: items.find((d) => d.key === m.defaultNavKey) ?? items[0],
        items,
      });
    }
    return out;
  }

  /** Every destination navigation may advertise (menus, ⌘K, pins): in a launchable module, with that module. */
  @computed get discoverable(): Array<{ destination: Destination; module: ResolvedModule }> {
    return this.modules.flatMap((module) => module.items.map((destination) => ({ destination, module })));
  }

  @computed private get ownerByKey(): Map<string, ResolvedModule> {
    return new Map(this.discoverable.map((d) => [d.destination.key, d.module]));
  }

  moduleOf(navKey: string): ResolvedModule | undefined {
    return this.ownerByKey.get(navKey);
  }

  isDiscoverable(navKey: string): boolean {
    return navKey === HOME_KEY || this.ownerByKey.has(navKey);
  }

  /**
   * Whether a link to a page or an entity (a Recent entry, `/agents/42`) may be advertised: it must resolve to a
   * destination navigation offers this person. Hiding a module, or moving its page into a hidden one, takes these
   * shortcuts away with the menu; the URL itself still opens from a bookmark under its own guards.
   */
  isLinkDiscoverable(href: string): boolean {
    const destination = findDestination(href.split(/[?#]/)[0]);
    return !!destination && this.isDiscoverable(destination.key);
  }

  /** Where a URL belongs. Detail routes inherit their parent destination (`/agents/42/briefings` → Agents → its module). */
  placementFor(pathname: string): PathPlacement {
    const destination = findDestination(pathname);
    if (!destination) return { kind: 'unknown' };
    if (destination.key === HOME_KEY) return { kind: 'home', destination };
    const module = this.ownerByKey.get(destination.key);
    if (module) return { kind: 'module', destination, module };
    const info = this.nav?.elsewhere.find((e) => e.navKey === destination.key);
    if (info) return { kind: 'elsewhere', destination, info };
    return { kind: 'not-in-navigation', destination };
  }

  /** The module whose menu the rail shows: the URL's module; on a page in none, the last one used; else the first. */
  railModule(pathname: string): ResolvedModule | undefined {
    const placement = this.placementFor(pathname);
    if (placement.kind === 'module') return placement.module;
    return this.modules.find((m) => m.key === this.lastModuleKey) ?? this.modules[0];
  }

  @action rememberModule(key: string): void {
    if (this.lastModuleKey === key) return;
    this.lastModuleKey = key;
    writePreference(LAST_MODULE_PREF, this.subject, key);
  }

  // ── Drift between this build and the server (admins see it on Administration → Modules) ──

  @computed get diagnostics(): {
    manifestMismatch: boolean;
    serverManifestVersion: number | null;
    clientManifestVersion: number;
    unsupportedKeys: string[];
    clientOnlyKeys: string[];
    routeMismatches: Array<{ navKey: string; server: string; client: string }>;
  } {
    const nav = this.nav;
    const advertised = nav ? [...nav.modules.flatMap((m) => m.items), ...nav.elsewhere] : [];
    const serverKeys = new Set(nav?.manifestKeys ?? []);
    return {
      manifestMismatch: !!nav && nav.manifestVersion !== STUDIO_MANIFEST_VERSION,
      serverManifestVersion: nav?.manifestVersion ?? null,
      clientManifestVersion: STUDIO_MANIFEST_VERSION,
      unsupportedKeys: nav ? [...serverKeys].filter((k) => !destinationByKey(k)).sort() : [],
      clientOnlyKeys: nav ? DESTINATIONS.map((d) => d.key).filter((k) => !serverKeys.has(k)) : [],
      routeMismatches: advertised
        .map((e) => ({ navKey: e.navKey, server: e.route, client: destinationByKey(e.navKey)?.to ?? '' }))
        .filter((r) => r.client && r.server !== r.client),
    };
  }
}
